import { memo, useEffect, useState, useRef, useCallback } from 'react';
import { CountUpProps } from './CountUp.types';
import Text from '../Text/Text';
import Box from '../Box/Box';

function CountUp({
  start,
  end,
  duration = 1000,
  visualIncrement = 0.01,
  increment = 0.01,
  useVisualIncrement = false,
  useFrameRestriction = false,
  formatValue = (value) => value,
  onValueChange,
  ...rest
}: CountUpProps) {
  const [value, setValue] = useState(end);
  const currentValue = useRef(start || end);
  /* We are rounding up the end value, that is used to stretch the container, to all 8s
    to ensure that the container is as wide as possible regardless of the combination
    of digits that the start value has.
  */
  const roundedEndValue = parseInt('8'.repeat(end.toString().length), 10);
  const showAnimation = useVisualIncrement && end > start;

  const calculateStepIncrement = useCallback(
    (diff: number) => {
      if (showAnimation) {
        const maxFPS = 60;
        const maxIterations = (duration / 1000) * maxFPS;
        const totalDifference = Math.abs(diff);
        const totalSteps = Math.ceil(totalDifference / visualIncrement);

        let adjustedVisualIncrement = visualIncrement;
        if (totalSteps > maxIterations && useFrameRestriction) {
          adjustedVisualIncrement = Math.ceil(totalDifference / maxIterations);
        }

        return diff > 0 ? adjustedVisualIncrement : -adjustedVisualIncrement;
      }

      return diff;
    },
    [showAnimation, visualIncrement, useFrameRestriction, duration],
  );

  const calculateStepInterval = useCallback(() => {
    if (showAnimation) {
      const stepsPerIncrement = increment / visualIncrement;
      const totalSteps = Math.abs(stepsPerIncrement);
      return duration / totalSteps;
    }
    return duration;
  }, [increment, visualIncrement, duration, showAnimation]);

  useEffect(() => {
    if (start === end) {
      setValue(end);
      onValueChange?.(end);
      return;
    }

    const diff = end - currentValue.current;
    const stepIncrement = calculateStepIncrement(diff);
    const stepInterval = calculateStepInterval();

    let requestID: number;
    let lastFrameTime: number | null = null;

    const count = (timestamp: number) => {
      if (lastFrameTime === null) lastFrameTime = timestamp;
      const elapsed = timestamp - lastFrameTime;

      if (elapsed >= stepInterval) {
        currentValue.current += stepIncrement;

        const completed =
          (diff > 0 && currentValue.current >= end) ||
          (diff < 0 && currentValue.current <= end);

        if (completed) {
          currentValue.current = end;
          setValue(end);
          onValueChange?.(end);
          return;
        }

        setValue(currentValue.current);
        onValueChange?.(currentValue.current);
        lastFrameTime = timestamp;
      }

      requestID = requestAnimationFrame(count);
    };

    requestID = requestAnimationFrame(count);

    return () => {
      cancelAnimationFrame(requestID);
    };
  }, [
    start,
    end,
    duration,
    visualIncrement,
    increment,
    calculateStepIncrement,
    calculateStepInterval,
    onValueChange,
  ]);

  return (
    <Box position="relative">
      <Text
        {...rest}
        numberOfLines={1}
        opacity={0}
        paddingHorizontal="space-025"
      >
        {`${formatValue(roundedEndValue)}`}
      </Text>
      <Text {...rest} position="absolute" top="space-0" numberOfLines={1}>
        {`${formatValue(value)}`}
      </Text>
    </Box>
  );
}

export default memo(CountUp);
